import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { ACCENT_BLUE, EASE_OUT, MULTICONNECTION_ROUTES, NOPING_GREEN } from '../constants';
import { AnimatedStat } from './animated-stat';

export function MulticonnectionVisual() {
  const { t } = useTranslation(['components/trial/lp/v2b']);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [animating, setAnimating] = useState(false);
  const [hoveredRoute, setHoveredRoute] = useState<number | null>(null);

  useEffect(() => {
    if (!isInView) return undefined;
    const timer = setTimeout(() => setAnimating(true), 300);
    return () => clearTimeout(timer);
  }, [isInView]);

  const routes = MULTICONNECTION_ROUTES;

  return (
    <motion.div ref={ref} className='relative mx-auto w-full max-w-3xl' initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE_OUT }}>
      <div className='relative overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D12]'>
        <div className='to-[#2ECDFF]/3 pointer-events-none absolute inset-0 bg-gradient-to-br from-[#93FF18]/5 via-transparent' />

        {/* Header */}
        <div className='flex items-center justify-between border-b border-white/5 px-5 py-3'>
          <div className='flex items-center gap-2'>
            <div className='size-2 rounded-full bg-[#93FF18]' />
            <span className='text-[10px] font-semibold uppercase tracking-widest' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
              {t('multiconnection.header_label')}
            </span>
          </div>
          <span className='rounded-full border px-2 py-0.5 text-[10px]' style={{ color: NOPING_GREEN, borderColor: `${NOPING_GREEN}30`, backgroundColor: `${NOPING_GREEN}08`, fontFamily: 'var(--font-jetbrains)' }}>
            {t('multiconnection.routes_active')}
          </span>
        </div>

        <div className='space-y-4 px-5 py-4'>
          {animating && (
            <motion.div className='grid grid-cols-2 gap-3' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.4 }}>
              {/* Without NoPing */}
              <div className='rounded-lg border border-[#2ECDFF]/15 bg-[#2ECDFF]/5 px-4 py-3'>
                <div className='mb-2 flex items-center gap-1.5'>
                  <div className='size-1.5 animate-pulse rounded-full bg-[#2ECDFF]' />
                  <span className='text-[9px] font-semibold uppercase tracking-wider' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-jetbrains)' }}>
                    {t('multiconnection.without_noping')}
                  </span>
                </div>
                <div className='grid grid-cols-3 gap-2'>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${ACCENT_BLUE}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.ping')}
                    </div>
                    <AnimatedStat value={168} suffix='ms' color={ACCENT_BLUE} fluctuate={{ min: 150, max: 250 }} />
                  </div>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${ACCENT_BLUE}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.loss')}
                    </div>
                    <AnimatedStat value={4} suffix='%' color={ACCENT_BLUE} fluctuate={{ min: 3, max: 8 }} />
                  </div>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${ACCENT_BLUE}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.jitter')}
                    </div>
                    <AnimatedStat value={42} suffix='ms' color={ACCENT_BLUE} fluctuate={{ min: 30, max: 65 }} />
                  </div>
                </div>
              </div>

              {/* With NoPing */}
              <div className='rounded-lg border border-[#93FF18]/15 bg-[#93FF18]/5 px-4 py-3'>
                <div className='mb-2 flex items-center gap-1.5'>
                  <div className='size-1.5 rounded-full bg-[#93FF18]' />
                  <span className='text-[9px] font-semibold uppercase tracking-wider' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
                    {t('multiconnection.with_noping')}
                  </span>
                </div>
                <div className='grid grid-cols-3 gap-2'>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${NOPING_GREEN}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.ping')}
                    </div>
                    <AnimatedStat value={3} suffix='ms' color={NOPING_GREEN} fluctuate={{ min: 1, max: 5 }} />
                  </div>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${NOPING_GREEN}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.loss')}
                    </div>
                    <span className='text-xs font-bold tabular-nums' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
                      0.0%
                    </span>
                  </div>
                  <div>
                    <div className='mb-0.5 text-[8px] uppercase' style={{ color: `${NOPING_GREEN}50`, fontFamily: 'var(--font-jetbrains)' }}>
                      {t('multiconnection.jitter')}
                    </div>
                    <AnimatedStat value={1} suffix='ms' color={NOPING_GREEN} fluctuate={{ min: 0, max: 2 }} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* SVG route visualization */}
          <div className='relative w-full overflow-x-auto'>
            <svg viewBox='0 0 700 200' className='h-auto w-full min-w-[460px]' fill='none'>
              <defs>
                <filter id='pt-glow-optimal' x='-50%' y='-50%' width='200%' height='200%'>
                  <feGaussianBlur stdDeviation='3' result='blur' />
                  <feMerge>
                    <feMergeNode in='blur' />
                    <feMergeNode in='SourceGraphic' />
                  </feMerge>
                </filter>
                <filter id='pt-glow-packet' x='-200%' y='-200%' width='500%' height='500%'>
                  <feGaussianBlur stdDeviation='2.5' result='blur' />
                  <feMerge>
                    <feMergeNode in='blur' />
                    <feMergeNode in='SourceGraphic' />
                  </feMerge>
                </filter>
              </defs>

              {/* YOU node */}
              <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={animating ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.4 }}>
                <rect x='2' y='82' width='54' height='36' rx='6' fill='#0a1a02' stroke={NOPING_GREEN} strokeWidth='1.5' />
                <text x='29' y='97' fill={NOPING_GREEN} fontSize='9' fontFamily='monospace' textAnchor='middle' fontWeight='bold'>
                  {t('multiconnection.you')}
                </text>
                <text x='29' y='110' fill={`${NOPING_GREEN}70`} fontSize='7' fontFamily='monospace' textAnchor='middle'>
                  L.A.
                </text>
              </motion.g>

              {/* GAME SERVER node */}
              <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={animating ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.4, delay: 0.8 }}>
                <rect x='610' y='72' width='86' height='56' rx='6' fill='#0a1a02' stroke={NOPING_GREEN} strokeWidth='1.5' />
                <text x='653' y='95' fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' textAnchor='middle' fontWeight='bold'>
                  {t('multiconnection.game')}
                </text>
                <text x='653' y='107' fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' textAnchor='middle' fontWeight='bold'>
                  {t('multiconnection.server')}
                </text>
                <text x='653' y='120' fill={`${NOPING_GREEN}60`} fontSize='7' fontFamily='monospace' textAnchor='middle'>
                  WASH. D.C.
                </text>
              </motion.g>

              {routes.map((route, ri) => {
                const pathD = route.hops.map((h, i) => `${i === 0 ? 'M' : 'L'} ${h.x} ${h.y}`).join(' ');
                const isHovered = hoveredRoute === ri;
                const isOptimal = route.optimal;
                const baseOpacity = isOptimal ? 0.85 : 0.25;
                const activeOpacity = isHovered ? 0.85 : baseOpacity;
                const getStrokeW = () => {
                  if (isOptimal) return 2.5;
                  if (isHovered) return 2;
                  return 1;
                };
                const strokeW = getStrokeW();
                const pathId = `pt-route-path-${ri}`;

                return (
                  <g key={`route-${ri}`} style={{ cursor: 'pointer' }} onMouseEnter={() => setHoveredRoute(ri)} onMouseLeave={() => setHoveredRoute(null)}>
                    <path d={pathD} stroke='transparent' strokeWidth='16' fill='none' />
                    <path id={pathId} d={pathD} fill='none' stroke='none' />

                    <motion.path d={pathD} stroke={NOPING_GREEN} strokeWidth={strokeW} strokeOpacity={activeOpacity} fill='none' filter={isOptimal || isHovered ? 'url(#pt-glow-optimal)' : undefined} initial={{ pathLength: 0 }} animate={animating ? { pathLength: 1 } : {}} transition={{ duration: 1.0, delay: ri * 0.12, ease: EASE_OUT }} />

                    {route.hops.map((hop, hi) => (
                      <motion.g key={`hop-${ri}-${hi}`}>
                        <motion.circle cx={hop.x} cy={hop.y} r={isOptimal || isHovered ? 4 : 3} fill={isOptimal ? '#0a1a02' : '#0D0D12'} stroke={NOPING_GREEN} strokeWidth={isOptimal || isHovered ? 1.5 : 0.8} strokeOpacity={activeOpacity} initial={{ scale: 0, opacity: 0 }} animate={animating ? { scale: 1, opacity: 1 } : {}} transition={{ delay: ri * 0.12 + hi * 0.06, duration: 0.25 }} />
                        {(isOptimal || isHovered) && hi > 0 && hi < route.hops.length - 1 && (
                          <motion.text x={hop.x} y={hop.y - 8} fill={NOPING_GREEN} fontSize='7' fontFamily='monospace' textAnchor='middle' opacity={activeOpacity} initial={{ opacity: 0 }} animate={{ opacity: activeOpacity }} transition={{ delay: 0.2 }}>
                            {hop.city}
                          </motion.text>
                        )}
                        {(isOptimal || isHovered) && hi < route.latencies.length && (
                          <motion.text x={(hop.x + route.hops[Math.min(hi + 1, route.hops.length - 1)]!.x) / 2} y={hop.y + (ri < 2 ? 15 : -5)} fill={`${NOPING_GREEN}80`} fontSize='6' fontFamily='monospace' textAnchor='middle' initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 0.3 }}>
                            {route.latencies[hi]}ms
                          </motion.text>
                        )}
                      </motion.g>
                    ))}

                    {animating && (
                      <>
                        <circle r={isOptimal ? 4 : 2.5} fill={NOPING_GREEN} opacity={isOptimal ? 0.9 : 0.5} filter={isOptimal ? 'url(#pt-glow-optimal)' : 'url(#pt-glow-packet)'}>
                          <animateMotion dur={`${route.packetDur}s`} repeatCount='indefinite' keyPoints='0;1' keyTimes='0;1' calcMode='linear'>
                            <mpath xlinkHref={`#${pathId}`} />
                          </animateMotion>
                        </circle>
                        <circle r={isOptimal ? 2.5 : 1.5} fill={NOPING_GREEN} opacity={isOptimal ? 0.5 : 0.2}>
                          <animateMotion dur={`${route.packetDur}s`} repeatCount='indefinite' keyPoints='0;1' keyTimes='0;1' calcMode='linear' begin={`${route.packetDur * 0.5}s`}>
                            <mpath xlinkHref={`#${pathId}`} />
                          </animateMotion>
                        </circle>
                      </>
                    )}

                    {isHovered && (
                      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
                        <rect x={route.hops[2]!.x - 28} y={route.hops[2]!.y + (ri < 3 ? 16 : -26)} width='56' height='18' rx='4' fill='#080809' stroke={NOPING_GREEN} strokeWidth='0.8' strokeOpacity='0.5' />
                        <text x={route.hops[2]!.x} y={route.hops[2]!.y + (ri < 3 ? 29 : -14)} fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' textAnchor='middle' fontWeight='bold'>
                          {route.total}ms total
                        </text>
                      </motion.g>
                    )}
                  </g>
                );
              })}

              {/* Best route badge */}
              {animating && (
                <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.4 }}>
                  <rect x='275' y='36' width='74' height='16' rx='4' fill='#93FF18' fillOpacity='0.12' stroke='#93FF18' strokeWidth='0.5' strokeOpacity='0.35' />
                  <text x='312' y='47' fill='#93FF18' fontSize='7' fontFamily='monospace' textAnchor='middle' fontWeight='bold'>
                    {t('multiconnection.best')}
                  </text>
                </motion.g>
              )}
            </svg>
          </div>

          {/* Footer legend */}
          <div className='flex items-center justify-between border-t border-white/5 pt-1 text-[9px]' style={{ fontFamily: 'var(--font-jetbrains)' }}>
            <span style={{ color: `${NOPING_GREEN}80` }}>
              <span className='me-1.5 inline-block h-px w-5 align-middle' style={{ backgroundColor: NOPING_GREEN }} />
              {t('multiconnection.legend_bridges')}
            </span>
            <span style={{ color: '#92929850' }}>{t('multiconnection.hover_inspect')}</span>
            <span className='flex items-center gap-1' style={{ color: NOPING_GREEN }}>
              <span className='inline-block size-2 rounded-full border' style={{ borderColor: `${NOPING_GREEN}60`, backgroundColor: `${NOPING_GREEN}15` }} />
              {t('multiconnection.optimal_route')}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
